import { Card, CardBody, CardFooter } from '@heroui/react'
import { Zap, Star, Target, Film, Flame, FileText, type LucideIcon } from 'lucide-react'
import { LAYOUT_TEMPLATES, EMPTY_TEMPLATE } from './templates/layout-templates'
import type { LayoutTemplate } from './templates/layout-templates'
import type { PageBlock } from '../../types'

const THUMBNAIL_ICONS: Record<string, LucideIcon> = {
  Zap,
  Star,
  Target,
  Film,
  Flame,
  FileText,
}

function ThumbnailIcon({ name, className }: { name: string; className?: string }) {
  const Icon = THUMBNAIL_ICONS[name]
  if (!Icon) return <span className={className}>{name}</span>
  return <Icon className={className} />
}

interface LayoutSelectorProps {
  onSelectTemplate: (blocks: PageBlock[]) => void
}

function TemplateCard({ template, onSelect }: { template: LayoutTemplate; onSelect: () => void }) {
  return (
    <Card
      isPressable
      onPress={onSelect}
      className="border border-default-200 hover:border-primary hover:shadow-md transition-all cursor-pointer animate-fade-slide-in"
    >
      <CardBody className="p-4">
        <div className="flex items-center justify-center w-full h-24 bg-default-100 rounded-lg mb-3">
          <ThumbnailIcon name={template.thumbnail} className="w-12 h-12 text-default-500" />
        </div>
        <h3 className="text-sm font-semibold text-default-700 mb-1">{template.name}</h3>
        <p className="text-xs text-default-500 line-clamp-2">{template.description}</p>
      </CardBody>
      <CardFooter className="pt-0 pb-3 px-4">
        <div className="flex items-center justify-between w-full">
          <span className="text-xs text-default-400">{template.blocks.length} blocks</span>
          <span className="text-xs text-primary font-medium">Use template →</span>
        </div>
      </CardFooter>
    </Card>
  )
}

export function LayoutSelector({ onSelectTemplate }: LayoutSelectorProps) {
  const handleSelectTemplate = (template: LayoutTemplate) => {
    const blocks: PageBlock[] = template.blocks.map((block, index) => ({
      id: `block-${Date.now()}-${index}`,
      type: block.type,
      props: { ...block.props },
    }))
    onSelectTemplate(blocks)
  }

  return (
    <div className="h-full flex items-center justify-center bg-default-50 p-8">
      <div className="max-w-5xl w-full">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-default-800 mb-2">Choose a Layout Template</h1>
          <p className="text-default-600">
            Start with a professionally designed template or build from scratch
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
          {LAYOUT_TEMPLATES.map((template) => (
            <TemplateCard
              key={template.id}
              template={template}
              onSelect={() => handleSelectTemplate(template)}
            />
          ))}
        </div>

        <div className="flex justify-center">
          <Card
            isPressable
            onPress={() => handleSelectTemplate(EMPTY_TEMPLATE)}
            className="max-w-md w-full border-2 border-dashed border-default-300 hover:border-primary hover:bg-default-100 transition-all cursor-pointer"
          >
            <CardBody className="p-6">
              <div className="flex items-center gap-4">
                <div className="flex items-center justify-center w-16 h-16 bg-default-100 rounded-lg flex-shrink-0">
                  <ThumbnailIcon name={EMPTY_TEMPLATE.thumbnail} className="w-8 h-8 text-default-400" />
                </div>
                <div className="flex-1">
                  <h3 className="text-base font-semibold text-default-700 mb-1">
                    {EMPTY_TEMPLATE.name}
                  </h3>
                  <p className="text-sm text-default-500">{EMPTY_TEMPLATE.description}</p>
                </div>
                <span className="text-sm text-primary font-medium whitespace-nowrap">Start →</span>
              </div>
            </CardBody>
          </Card>
        </div>
      </div>
    </div>
  )
}
